// src/pages/CreateEventPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path
import { useAuth } from '../context/AuthContext'; // Adjust path
import styles from '../styles/FormPage.module.css'; // Use a generic form style or create a new one

// Helper to format date as YYYY-MM-DD
const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const CreateEventPage = () => {
    const { currentUser, userRole } = useAuth();
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [eventDate, setEventDate] = useState(''); // Store as YYYY-MM-DD string
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Redirect if user is not a manager or not logged in
    useEffect(() => {
        if (!currentUser || (userRole && userRole !== 'Manager' && userRole !== 'Accounts Manager')) {
            console.log("Access denied. Redirecting...");
            navigate('/dashboard'); // Or to a specific unauthorized page
        }
    }, [currentUser, userRole, navigate]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!title.trim() || !eventDate) {
            setError('Event title and date are required.');
            return;
        }

        setLoading(true);

        try {
            const eventsCol = collection(db, 'events');
            await addDoc(eventsCol, {
                title: title.trim(),
                // Store date as YYYY-MM-DD string for easier querying
                date: eventDate,
                description: description.trim(),
                createdAt: serverTimestamp(),
                createdBy: currentUser.uid,
                creatorName: currentUser.displayName || currentUser.email, // Optional: store creator's name
                type: 'custom', // Indicate it's a manually added event
            });

            setSuccessMessage('Event created successfully!');
            // Clear form
            setTitle('');
            setEventDate('');
            setDescription('');
            // Optionally navigate back or stay to add more
            // navigate('/dashboard');

        } catch (err) {
            console.error("Error creating event:", err);
            setError('Failed to create event. Please try again.');
        } finally {
            setLoading(false);
        }
    };

     // Prevent non-managers from seeing the form even briefly before redirect
     if (!currentUser || (userRole && userRole !== 'Manager' && userRole !== 'Accounts Manager')) {
        return <div className={styles.loadingIndicator}>Checking permissions...</div>; // Or null
     }


    return (
        <div className={styles.formContainer}> {/* Use a generic container style */}
            <h2 className={styles.title}>Create New Event</h2>

            {error && <p className={styles.errorMessage}>{error}</p>}
            {successMessage && <p className={styles.successMessage}>{successMessage}</p>}

            <form onSubmit={handleSubmit} className={styles.form}>
                {/* Event Title */}
                <div className={styles.inputGroup}>
                    <label htmlFor="eventTitle">Event Title</label>
                    <input
                        type="text"
                        id="eventTitle"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        disabled={loading}
                        placeholder="e.g., Team Meeting, Public Holiday"
                    />
                </div>

                {/* Event Date */}
                <div className={styles.inputGroup}>
                    <label htmlFor="eventDate">Date</label>
                    <input
                        type="date"
                        id="eventDate"
                        value={eventDate}
                        onChange={(e) => setEventDate(e.target.value)}
                        required
                        disabled={loading}
                        // Set min date to today if desired
                        // min={formatDateToYYYYMMDD(new Date())}
                    />
                </div>

                {/* Event Description (Optional) */}
                <div className={styles.inputGroup}>
                    <label htmlFor="eventDescription">Description (Optional)</label>
                    <textarea
                        id="eventDescription"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows="3"
                        disabled={loading}
                        placeholder="Add any relevant details..."
                    />
                </div>

                {/* Submit Button */}
                <div className={styles.buttonGroup}>
                     <button type="button" onClick={() => navigate(-1)} className={`${styles.formButton} ${styles.cancelButton}`} disabled={loading}>
                         Cancel
                     </button>
                    <button type="submit" className={`${styles.formButton} ${styles.submitButton}`} disabled={loading}>
                        {loading ? 'Creating...' : 'Create Event'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateEventPage;
